import { useEffect } from 'react'
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import { useTranslation } from 'react-i18next'
import { colors, fonts, radius } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import RankBadge from '../RankBadge'
import PersonAvatarFace from '../PersonAvatarFace'
import { useAvatars } from '../../lib/useAvatars'
import { perfMarkOnce } from '../../lib/perfTrace'
import { RANK_COLORS as RING } from '../../lib/rankColors'

// Posizione di una riga (stesse chiavi usate dalle varie classifiche)
export const posOf = (row, i) => row?.posizione_classifica ?? row?.posizione ?? row?.posizione_gc ?? (i + 1)
// Posizioni condivise da piu' giocatori (pari merito): serve a mostrare "ex aequo" e a non far sembrare un errore il secondo 3°
export function tiedPositions(data) {
  const count = {}
  data.forEach((r, i) => { const p = posOf(r, i); count[p] = (count[p] ?? 0) + 1 })
  return new Set(Object.keys(count).filter(p => count[p] > 1).map(Number))
}

// "HH:MM:SS" (o "N days HH:MM:SS") -> secondi
export function toSeconds(g) {
  const m = String(g ?? '').match(/(?:(\d+) days? )?(\d+):(\d+):(\d+)/)
  if (!m) return null
  return (m[1] ? Number(m[1]) * 86400 : 0) + Number(m[2]) * 3600 + Number(m[3]) * 60 + Number(m[4])
}
export function fmtSec(sec) {
  const s = Math.round(sec)
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = s % 60
  const pad = (n) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(ss)}` : `${m}:${pad(ss)}`
}

// ─── Variazione di posizione rispetto alla tappa precedente (solo classifiche generali) ───
// delta > 0 = salito, < 0 = sceso. Forma + colore + numero (il colore da solo non basta).
export function Trend({ delta }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  if (delta == null) return null
  if (delta === 0) return <Text style={[styles.trendFlat, { color: tc.textMuted }]} accessibilityLabel={t('leaderboard.trend_same')}>–</Text>
  const up = delta > 0
  const col = up ? colors.success : tc.rosaText
  return (
    <View style={styles.trend} accessible accessibilityLabel={up ? t('leaderboard.trend_up', { n: delta }) : t('leaderboard.trend_down', { n: -delta })}>
      <MaterialCommunityIcons name={up ? 'menu-up' : 'menu-down'} size={18} color={col} style={{ marginRight: -3 }} />
      <Text style={[styles.trendNum, { color: col }]}>{Math.abs(delta)}</Text>
    </View>
  )
}

// ─── La tua posizione ─────────────────────────────────────────────────
// Se non sei nel podio, una riga in cima alla classifica ti dice dove sei senza dover scorrere; al tocco apre la tua scheda.
export function MyRank({ data, currentUserId, valueKey, valueLabel, valueFmt, tied, onPlayerClick, nameKey = 'display_name', numOf, isTime = false, lowerIsBetter = false, delta }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const idx = data.findIndex(r => r?.id_user === currentUserId)
  if (!currentUserId || idx < 0) return null
  const row = data[idx]
  const pos = posOf(row, idx)
  if (pos <= 3) return null                       // gia' visibile nel podio
  const valueText = valueFmt ? valueFmt(row) : `${row[valueKey]}${valueLabel ? ` ${valueLabel}` : ''}`

  // distacco dal giocatore sopra (da recuperare) e da quello sotto (di vantaggio)
  const above = data[idx - 1], below = data[idx + 1]
  const mine = numOf?.(row)
  const gapTo = (other, sign) => {
    const n = other ? numOf?.(other) : null
    if (mine == null || n == null) return null
    const d = lowerIsBetter ? (sign > 0 ? mine - n : n - mine) : (sign > 0 ? n - mine : mine - n)
    return d > 0 ? (isTime ? fmtSec(d) : `${d} pt`) : null
  }
  const gapUp = gapTo(above, 1), gapDown = gapTo(below, -1)
  return (
    <TouchableOpacity
      onPress={() => onPlayerClick?.({ id: currentUserId, name: row[nameKey], pos, value: valueText })}
      activeOpacity={0.8}
      accessibilityRole="button"
      accessibilityLabel={`${t('leaderboard.my_rank')}: ${pos}, ${valueText}`}
      style={[styles.myRank, { backgroundColor: tc.rosaSubtle, borderColor: tc.rosaBorder }]}
    >
      <PersonAvatarFace userId={currentUserId} size={34} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={[styles.myRankLabel, { color: tc.textMuted }]}>{t('leaderboard.my_rank')}</Text>
        <Text style={[styles.myRankPos, { color: tc.rosaText }]}>
          {pos}°{tied?.has(pos) ? <Text style={styles.myRankTie}>{`  ${t('leaderboard.tie')}`}</Text> : null}
        </Text>
        {(gapUp || gapDown) && (
          <Text style={[styles.myRankGaps, { color: tc.textMuted }]} numberOfLines={1}>
            {[gapUp && t('leaderboard.gap_above', { gap: gapUp, pos: posOf(above, idx - 1) }), gapDown && t('leaderboard.gap_below', { gap: gapDown, pos: posOf(below, idx + 1) })].filter(Boolean).join('  ·  ')}
          </Text>
        )}
      </View>
      <Trend delta={delta ?? row.delta} />
      <Text style={[styles.rowValue, { color: tc.text }]}>{valueText}</Text>
    </TouchableOpacity>
  )
}

// ─── Podio ────────────────────────────────────────────────────────────
// PWA: ordine [2°, 1°, 3°], altezze [88, 110, 72]
// 1° ha bg rosa, altri bg-card2
// faces: mostra le facce (default: solo se la chiave e' id_user; altre classifiche con id utente, come la stagionale, lo passano esplicito)
export function Podio({ data, nameKey, valueKey, valueLabel, currentUserId, userIdKey, onPlayerClick, faces = userIdKey === 'id_user', valueFmt, tied, renderSub, showTrend = false }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const top3 = data.slice(0, 3)
  const avatars = useAvatars(faces && userIdKey ? top3.map(r => r?.[userIdKey]) : [])
  // Ordine visivo: [2°, 1°, 3°]
  const order  = [top3[1], top3[0], top3[2]]
  const idxOf  = [1, 0, 2]
  const heights = [88, 110, 72]

  return (
    <View style={styles.podioRow}>
      {order.map((item, i) => {
        if (!item) return <View key={i} style={{ flex: i === 1 ? 1.2 : 1 }} />
        const rank  = posOf(item, idxOf[i])             // posizione reale: con un pari merito anche il terzo puo' essere 2°
        const isTie = tied?.has(rank)
        const valueText = valueFmt ? valueFmt(item) : `${item[valueKey]}${valueLabel ? ` ${valueLabel}` : ''}`
        const isMe  = userIdKey ? item[userIdKey] === currentUserId : false
        const uid   = userIdKey ? item[userIdKey] : null
        const isFirst = i === 1

        return (
          <TouchableOpacity
            key={i}
            style={{ flex: isFirst ? 1.2 : 1, alignItems: 'center', gap: 6, minWidth: 0 }}
            onPress={() => uid && onPlayerClick?.({ id: uid, name: item[nameKey], pos: rank, value: valueText })}
            activeOpacity={uid ? 0.7 : 1}
            accessibilityRole={uid ? 'button' : undefined}
            accessibilityLabel={`${rank}${isTie ? ` ${t('leaderboard.tie')}` : ''}, ${item[nameKey]}, ${valueText}`}
          >
            {uid && faces && <PersonAvatarFace userId={uid} settings={avatars[uid]} size={isFirst ? 56 : 48} ring={RING[rank]} />}
            <Text
              style={[
                styles.podioName,
                { color: isMe ? tc.rosaText : tc.text },
                isFirst && styles.podioNameFirst,
              ]}
              numberOfLines={2}
            >
              {item[nameKey]}
            </Text>
            {renderSub?.(item)}
            {showTrend && <Trend delta={item.delta} />}
            <View style={[
              styles.podioBlock,
              {
                height: heights[i],
                backgroundColor: isFirst ? tc.rosa : tc.bgCard2,
                borderColor: isMe || isFirst ? tc.rosa : tc.border,
                borderWidth: isMe ? 2 : 1,
              },
            ]}>
              <Text style={[styles.podioRank, { color: isFirst ? tc.brandText : tc.rosaText, fontSize: isFirst ? 30 : 24 }]}>{rank}</Text>
              {isTie && <Text style={[styles.podioTie, { color: isFirst ? tc.brandText : tc.textMuted }]}>{t('leaderboard.tie')}</Text>}
              <Text style={[styles.podioValue, { color: isFirst ? tc.brandText : tc.text, fontSize: isFirst ? 17 : 15 }]}>{valueText}</Text>
            </View>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

// ─── ListaClassifica ─────────────────────────────────────────────────
// PWA: padding '10px 12px', borderRadius 10, bg rosaSutble/bgCard2, gap 4
export function ListaClassifica({ data, nameKey, valueKey, valueLabel, currentUserId, userIdKey, onPlayerClick, valueFmt, tied, renderSub, offset = 0, showTrend = false }) {
  const { t } = useTranslation()
  const tc = useThemeColors()
  const avatars = useAvatars(userIdKey === 'id_user' ? data.map(r => r?.[userIdKey]) : [])
  useEffect(() => { perfMarkOnce('list_committed') }, [])   // la lista e' stata disegnata (misura dei tempi, solo admin)

  return (
    <View style={{ gap: 4 }}>
      {data.map((row, i) => {
        const pos   = posOf(row, i + offset)
        const isTie = tied?.has(pos)
        const valueText = valueFmt ? valueFmt(row) : `${row[valueKey]}${valueLabel ? ` ${valueLabel}` : ''}`
        const isMe  = userIdKey ? row[userIdKey] === currentUserId : false
        const uid   = userIdKey ? row[userIdKey] : null

        return (
          <TouchableOpacity
            key={i}
            onPress={() => uid && onPlayerClick?.({ id: uid, name: row[nameKey], pos, value: valueText })}
            activeOpacity={uid ? 0.7 : 1}
            style={[
              styles.row,
              {
                backgroundColor: isMe ? tc.rosaSubtle : tc.bgCard2,
                borderColor:     isMe ? tc.rosaBorder  : 'transparent',
              },
            ]}
          >
            <View style={styles.rowLeft}>
              <RankBadge pos={pos} size={16} />
              {uid && userIdKey === 'id_user' && <PersonAvatarFace userId={uid} settings={avatars[uid]} size={34} ring={RING[pos]} />}
              <View style={{ flexShrink: 1 }}>
                <Text style={[styles.rowName, { color: isMe ? tc.rosaText : tc.text, fontWeight: isMe ? '700' : '500' }]} numberOfLines={1}>
                  {row[nameKey]}
                  {isTie ? <Text style={[styles.rowTie, { color: tc.textMuted }]}>{`  ${t('leaderboard.tie')}`}</Text> : null}
                </Text>
                {renderSub?.(row)}
              </View>
            </View>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              {showTrend && <Trend delta={row.delta} />}
              <Text style={[styles.rowValue, { color: tc.text }]}>{valueText}</Text>
            </View>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

const styles = StyleSheet.create({
  // Podio
  podioRow:        { flexDirection: 'row', alignItems: 'flex-end', gap: 10, paddingHorizontal: 16, marginBottom: 20 },
  podioRank:       { fontFamily: fonts.condensedBold },
  podioName:       { fontFamily: fonts.condensedBold, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center' },
  podioNameFirst:  { fontSize: 14 },
  podioBlock:      { borderRadius: 10, width: '100%', alignItems: 'center', justifyContent: 'center', gap: 2 },
  podioValue:      { fontFamily: fonts.condensedBold },
  podioValueLabel: { fontSize: 10 },
  podioTie:        { fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.5, fontFamily: fonts.condensedBold },

  // La tua posizione
  myRank:      { flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderRadius: 12, padding: 10, paddingHorizontal: 12, marginBottom: 14 },
  myRankLabel: { fontSize: 10, fontFamily: fonts.condensedBold, textTransform: 'uppercase', letterSpacing: 0.6 },
  myRankPos:   { fontSize: 20, fontFamily: fonts.condensedBold },
  myRankTie:   { fontSize: 11, textTransform: 'uppercase' },
  myRankGaps:  { fontSize: 12, marginTop: 1 },
  trend:       { flexDirection: 'row', alignItems: 'center', marginRight: 4 },
  trendNum:    { fontFamily: fonts.condensedBold, fontSize: 13 },
  trendFlat:   { fontFamily: fonts.condensedBold, fontSize: 14, minWidth: 14, textAlign: 'center' },

  // ListaClassifica
  row:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', padding: 10, paddingHorizontal: 12, borderRadius: 10, borderWidth: 1 },
  rowLeft:  { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 },
  rowRank:  { fontSize: 14, fontWeight: '700', minWidth: 28 },
  rowName:  { fontSize: 14 },
  rowValue: { fontSize: 14, fontFamily: fonts.condensedBold },
  rowTie:   { fontSize: 10, fontFamily: fonts.condensedBold, textTransform: 'uppercase' },
})